import { useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import { useInterval } from "react-use";

import type { ICartAddModifierChoice } from "@/api/interfaces/cartLayouts";
import type {
  IModifier,
  IModifierChoice,
} from "@/api/interfaces/productLayouts";
import { ProductModifierSelectionMode } from "@/api/interfaces/productLayouts";
import type { ProductItem } from "@/store/products/types";

import { ExtrasImgRow } from "./ExtrasImgRow";
import { ExtrasRow } from "./ExtrasRow";

const useStyles = createUseStyles({
  extrasRow: {
    background: "transparent",
    padding: 0,
    display: "flex",
    alignItems: "center",
    width: "100%",
    cursor: "pointer",
  },
});

type AddItem = {
  idToAdd?: number;
  idToRemove?: number;
  price: number;
  choiceId?: number;
};

type ModifierChoiceProps = {
  modifier: IModifier;
  choice: IModifierChoice;
  product: ProductItem | undefined;
  cartModifiers: ICartAddModifierChoice[];
  setCartModifiers: (value: ICartAddModifierChoice[]) => void;
  setHasMaxSelectionChoiceError: (value: boolean) => void;
  blockAddedItems: number[];
  addedItems: number[];
  previousSelection: number | undefined;
  setPreviousSelection: (value: number) => void;
  isMandatory: boolean;
  isTextual: boolean;
  hasDefaultSelected: boolean;
  setHasDefaultSelected: (value: boolean) => void;
};

export const ModifierChoice = ({
  modifier,
  choice,
  product,
  cartModifiers,
  setCartModifiers,
  setHasMaxSelectionChoiceError,
  blockAddedItems,
  addedItems,
  previousSelection,
  setPreviousSelection,
  isMandatory,
  isTextual,
  hasDefaultSelected,
  setHasDefaultSelected,
}: ModifierChoiceProps) => {
  const classes = useStyles();

  const [isRowAdded, setIsRowAdded] = useState<boolean>(
    cartModifiers.map((m) => m.ProductModifierChoiceID).includes(choice.ID),
  );
  const [shouldShowConstraintMsg, setShouldShowConstraintMsg] =
    useState<boolean>(false);

  useInterval(
    () => setShouldShowConstraintMsg(false),
    shouldShowConstraintMsg ? 2000 : null,
  );

  useEffect(() => {
    if (addedItems.length < modifier.Constraint) {
      setShouldShowConstraintMsg(false);
    }
    if (addedItems.includes(choice.ID)) {
      if (isMandatory) {
        setHasDefaultSelected(true);
        setPreviousSelection(choice.ID);
      }
      setIsRowAdded(true);
    } else {
      setIsRowAdded(false);
    }
  }, [addedItems]);

  // Adds default item to cart
  useEffect(() => {
    if (isMandatory && choice.DefaultQuantity != 0) {
      rowActions(choice.ID, price, isRowAdded);
      setHasDefaultSelected(true);
    }
  }, []);

  const updateCartModifiers = (item: AddItem): void => {
    let cartItems: ICartAddModifierChoice[] = cartModifiers;
    if (item.idToRemove) {
      cartItems = cartItems.filter(
        (e) => e.ProductModifierChoiceID != item.idToRemove,
      );
    }

    if (item.idToAdd) {
      const newChoice: ICartAddModifierChoice = {
        ProductModifierID: modifier.ID,
        ProductModifierChoiceID: item.idToAdd,
        Amount: 1,
        ProductId: modifier.Choices.find(
          (choice) => choice.ID === item.choiceId,
        )!.ProductID,
      };
      cartItems = [...cartItems, newChoice];
    }

    setCartModifiers(cartItems);
  };

  const rowActions = (
    choiceID: number,
    productPrice: number,
    isRowAdded: boolean,
  ) => {
    setHasMaxSelectionChoiceError(false);
    const isReachedLimit =
      modifier.SelectionMode == ProductModifierSelectionMode.MULTIPLE_CHOICE &&
      blockAddedItems.length >= modifier.SelectionMaxChoiceCount &&
      modifier.SelectionMaxChoiceCount != 0;
    if (
      modifier.SelectionMode == ProductModifierSelectionMode.MULTIPLE_CHOICE &&
      previousSelection
    ) {
      if (isRowAdded) {
        updateCartModifiers({
          idToRemove: choiceID,
          price: productPrice,
          choiceId: choiceID,
        });
      } else {
        if (isReachedLimit) {
          document.querySelector(`#modifier-${modifier.ID}`)?.scrollIntoView({
            block: "start",
            behavior: "smooth",
          });
          setHasMaxSelectionChoiceError(true);
          return;
        }
        updateCartModifiers({
          idToAdd: choiceID,
          price: productPrice,
          choiceId: choiceID,
        });
      }
    } else {
      setHasDefaultSelected(true);
      if (isRowAdded && !isMandatory) {
        updateCartModifiers({
          idToRemove: choiceID,
          price: productPrice,
          choiceId: choiceID,
        });
      } else {
        updateCartModifiers({
          idToAdd: choiceID,
          idToRemove: previousSelection,
          price: productPrice,
          choiceId: choiceID,
        });
      }
    }

    setPreviousSelection(choiceID);
  };

  const price: number = product?.grossPrice || 0;

  return isTextual ? (
    <div
      className={classes.extrasRow}
      onClick={() => rowActions(choice.ID, price, isRowAdded)}
      id={choice.LocalName}
      key={choice.ID}
    >
      <ExtrasRow
        name={`${choice.LocalName || product?.title} ${
          choice.PublicDescription
        }`}
        price={price}
        isAdded={isRowAdded}
        isMandatory={isMandatory}
      />
    </div>
  ) : (
    <div
      key={choice.ID}
      onClick={() => rowActions(choice.ID, price, isRowAdded)}
    >
      <ExtrasImgRow
        name={`${choice.LocalName || product?.title} ${choice.PublicDescription}`}
        price={price}
        isAdded={isRowAdded}
        isMandatory={isMandatory}
        hasDefaultSelected={hasDefaultSelected}
        img={choice.MediaResources[0]?.ThumbnailLocation}
      />
    </div>
  );
};
